import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

export interface TooltipProps {
  text?: string | null;
  content?: ReactNode;
  children: ReactNode;
  className?: string;
  width?: 'default' | 'wide';
}

export function Tooltip({
  text,
  content,
  children,
  className,
  width = 'default',
}: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tooltipRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const tip = tooltipRef.current.getBoundingClientRect();
    const margin = 8;
    let left = trigger.left + trigger.width / 2 - tip.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - tip.width - margin));
    const top = trigger.bottom + 6;
    setCoords({ top, left });
  }, [open, text, content]);

  const body = content ?? text;
  if (!body) return <>{children}</>;

  return (
    <>
      <span
        ref={triggerRef}
        className={cn('inline-flex', className)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <span
            ref={tooltipRef}
            role="tooltip"
            style={coords ? { top: coords.top, left: coords.left } : { top: -9999, left: -9999 }}
            className={cn(
              'fixed z-50 block break-words rounded-md border border-outlines bg-white px-2.5 py-1.5 text-xs text-text-primary shadow-md',
              width === 'wide'
                ? 'max-w-sm pointer-events-auto'
                : 'max-w-[220px] pointer-events-none',
            )}
          >
            {body}
          </span>,
          document.body,
        )}
    </>
  );
}

// ── Compound API shims (TooltipProvider / TooltipTrigger / TooltipContent) ──
// Kept for backward compatibility with call sites that still use the Radix
// compound pattern. They delegate to the single Tooltip above.

export interface TooltipProviderProps {
  children: ReactNode;
  delayDuration?: number;
}
export function TooltipProvider({ children }: TooltipProviderProps) {
  return <>{children}</>;
}

export interface TooltipTriggerProps {
  children: ReactNode;
  asChild?: boolean;
}
// TooltipTrigger is rendered by Tooltip itself in custom-prop mode.
// For compound usage, just render the child — the outer Tooltip wraps it.
export function TooltipTrigger({ children }: TooltipTriggerProps) {
  return <>{children}</>;
}

export interface TooltipContentProps {
  children: ReactNode;
  className?: string;
  width?: 'default' | 'wide';
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  hidden?: boolean;
}
// TooltipContent is a no-op shim — content is passed via the `content` prop on Tooltip.
export function TooltipContent({ children, hidden = false }: TooltipContentProps) {
  if (hidden) return null;
  return <>{children}</>;
}
